import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { buildAddress } from "@/server/import/geocoder";
import type { AddressConfig } from "@/server/import/types";
import type { WizardState, WizardAction } from "../ImportPage";

interface Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}

const SKIP = "__skip__";

/** Patterns used to auto-detect address-related columns */
const ADDRESS_PATTERNS = [/address/i, /street/i, /straat/i, /adres/i];
const CITY_PATTERNS = [/city/i, /town/i, /stad/i, /plaats/i, /gemeente/i];
const STATE_PATTERNS = [/state/i, /province/i, /provincie/i, /region/i];
const ZIP_PATTERNS = [/zip/i, /postal/i, /postcode/i, /post.*code/i];

function autoDetect(
  headers: string[],
  patterns: RegExp[],
): string | null {
  for (const pattern of patterns) {
    const match = headers.find((h) => pattern.test(h));
    if (match) return match;
  }
  return null;
}

export function AddressStep({ state, dispatch }: Props) {
  const headers = state.parseResult?.headers ?? [];

  const [addressCol, setAddressCol] = useState<string>(
    () => autoDetect(headers, ADDRESS_PATTERNS) ?? SKIP,
  );
  const [cityCol, setCityCol] = useState<string>(
    () => autoDetect(headers, CITY_PATTERNS) ?? SKIP,
  );
  const [stateCol, setStateCol] = useState<string>(
    () => autoDetect(headers, STATE_PATTERNS) ?? SKIP,
  );
  const [zipCol, setZipCol] = useState<string>(
    () => autoDetect(headers, ZIP_PATTERNS) ?? SKIP,
  );

  const config: AddressConfig = {
    addressColumn: addressCol === SKIP ? null : addressCol,
    cityColumn: cityCol === SKIP ? null : cityCol,
    stateColumn: stateCol === SKIP ? null : stateCol,
    zipColumn: zipCol === SKIP ? null : zipCol,
  };

  const hasAtLeastOne = config.addressColumn || config.cityColumn;

  // Preview: first 5 rows with concatenated address
  const previewRows = useMemo(() => {
    if (!state.parseResult) return [];
    return state.parseResult.sampleRows.slice(0, 5).map((row, i) => ({
      index: i + 2,
      address: buildAddress(row, config),
    }));
  }, [state.parseResult, addressCol, cityCol, stateCol, zipCol]);

  const handleNext = () => {
    dispatch({ type: "SET_ADDRESS_CONFIG", addressConfig: config });
    dispatch({ type: "GOTO_STEP", step: "preview" });
  };

  const selectOptions = (
    <SelectContent>
      <SelectItem value={SKIP}>
        <span className="text-muted-foreground">-- Skip --</span>
      </SelectItem>
      {headers.map((h) => (
        <SelectItem key={h} value={h}>
          {h}
        </SelectItem>
      ))}
    </SelectContent>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-4 h-4" />
            Configure Address Fields
          </CardTitle>
          <CardDescription>
            No coordinate columns were detected. Map address fields below so
            we can geocode locations via the Google Geocoding API.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Street Address</label>
              <Select value={addressCol} onValueChange={setAddressCol}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                {selectOptions}
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">City</label>
              <Select value={cityCol} onValueChange={setCityCol}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                {selectOptions}
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">State / Province</label>
              <Select value={stateCol} onValueChange={setStateCol}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                {selectOptions}
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">ZIP / Postal Code</label>
              <Select value={zipCol} onValueChange={setZipCol}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                {selectOptions}
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview table */}
      {previewRows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Address Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left p-2 font-medium w-16">Row</th>
                    <th className="text-left p-2 font-medium">
                      Concatenated Address
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r) => (
                    <tr key={r.index} className="border-b last:border-b-0">
                      <td className="p-2 text-muted-foreground">{r.index}</td>
                      <td className="p-2 font-mono text-xs">
                        {r.address || (
                          <span className="text-muted-foreground italic">
                            (empty)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!hasAtLeastOne && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          At least a street address or city column must be selected.
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => dispatch({ type: "GOTO_STEP", step: "mapping" })}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button disabled={!hasAtLeastOne} onClick={handleNext}>
          Next
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
